// MARK://dhk8/pos/lib/security-policy
// One place for the "what needs a manager PIN" policy: the list of gated actions
// (so a reviewer can see at a glance what is protected) and the threshold constants
// that decide *when* a borderline action (a big discount, a big cash variance) trips
// the gate. Pure data + tiny predicates — no React, no I/O.
//
// In a PREVIEW_STUB / dev-login build the gate accepts a fixed demo PIN
// (DEMO_MANAGER_PIN) so the showcase build stays usable without an auth backend;
// real builds verify the PIN against dhk8-platform-auth (see features/security/usePinGate).

import { PREVIEW_STUB } from './seed-preview.js'

/** Demo manager PIN — only honoured when PREVIEW_STUB (or VITE_DEV_LOGIN) is on.
 *  Lane #172 — alineado a 6 dígitos (backend `randomDigits(6)`). */
export const DEMO_MANAGER_PIN = '123456'

/** Demo terminal-unlock PIN (the LockScreen takes 6 digits) — demo builds only. */
export const DEMO_UNLOCK_PIN = '123456'

/** Whether the gate runs in "demo" mode (fixed PIN, surfaced in the prompt helper text). */
export const PIN_GATE_DEMO_MODE =
  PREVIEW_STUB || (import.meta.env['VITE_DEV_LOGIN'] as string | undefined) === '1'

/** Length of the manager PIN the prompt collects.
 *  Lane #172 — alineado con backend `randomDigits(6)` (pin.routes.ts). 6 dígitos. */
export const MANAGER_PIN_LENGTH = 6

/** Sensitive actions that require a manager PIN. Keys are referenced by callers. */
export type GatedAction =
  | 'order.voidItem'        // quitar / anular un ítem de la comanda
  | 'order.voidOrder'       // anular la comanda completa
  | 'order.bigDiscount'     // descuento por encima de la política
  | 'order.priceOverride'   // sobreescribir precio / línea
  | 'order.compItem'        // cortesía / regalo de un ítem
  | 'ticket.reopen'         // reabrir un ticket cerrado
  | 'ticket.refund'         // cancelar / reembolsar un ticket pagado
  | 'floor.deleteTable'     // eliminar una mesa (modo edición)
  | 'floor.forceClear'      // forzar liberar una mesa ocupada
  | 'cash.bigVariance'      // cerrar caja con diferencia grande
  | 'cash.cancelShift'      // cancelar / reabrir el turno de caja
  | 'cash.drop'             // retiro de efectivo (mid-turno)
  | 'cash.pickup'           // ingreso de efectivo (mid-turno)
  | 'cash.xreport'          // corte X (lectura del turno)
  | 'cash.close'            // corte Z / cierre de caja

/** Short human label for each gated action (used in the prompt subtitle). */
export const GATED_ACTION_LABEL: Record<GatedAction, string> = {
  'order.voidItem': 'Anular ítem',
  'order.voidOrder': 'Anular comanda',
  'order.bigDiscount': 'Descuento fuera de política',
  'order.priceOverride': 'Cambiar precio de línea',
  'order.compItem': 'Cortesía / regalo',
  'ticket.reopen': 'Reabrir ticket',
  'ticket.refund': 'Cancelar / reembolsar ticket',
  'floor.deleteTable': 'Eliminar mesa',
  'floor.forceClear': 'Forzar liberar mesa',
  'cash.bigVariance': 'Cierre con diferencia',
  'cash.cancelShift': 'Cancelar turno',
  'cash.drop': 'Retiro de efectivo',
  'cash.pickup': 'Ingreso de efectivo',
  'cash.xreport': 'Corte X',
  'cash.close': 'Cierre de caja (Corte Z)',
}

// ── Thresholds ─────────────────────────────────────────────────────────────

/** A discount trips the gate when it exceeds *either* of these (percent OR pesos). */
export const DISCOUNT_GATE_PERCENT = 15        // > 15 %
export const DISCOUNT_GATE_AMOUNT_CENTS = 200_00 // > $200.00

/** A cash close trips the gate when |contado − esperado| exceeds this. */
export const CASH_VARIANCE_GATE_CENTS = 100_00 // > $100.00

/** True when a discount of `kind` and `value` (percent or pesos) needs a manager PIN. */
export function discountNeedsGate(kind: 'PERCENT' | 'AMOUNT', value: number, baseCents: number): boolean {
  if (!Number.isFinite(value) || value <= 0) return false
  if (kind === 'PERCENT') {
    if (value > DISCOUNT_GATE_PERCENT) return true
    return Math.round((baseCents * Math.min(100, value)) / 100) > DISCOUNT_GATE_AMOUNT_CENTS
  }
  return Math.round(value * 100) > DISCOUNT_GATE_AMOUNT_CENTS
}

/** True when a cash variance (in cents, signed or not) needs a manager PIN to confirm the close. */
export function varianceNeedsGate(varianceCents: number): boolean {
  return Math.abs(varianceCents) > CASH_VARIANCE_GATE_CENTS
}
